import { formatPrice } from '@utils/prices';
import { useTranslation } from 'next-i18next';

import { SelectableTile } from '../../../../design-system/components/SelectableTile';
import { CurrencyCode } from '../../../../types/Locations';

type WorkSelectionGridProps = {
  allowMultipleSelections?: boolean;
  currencyCode: CurrencyCode;
  enteredItemIds?: string[];
  isPreOrderFlow?: boolean;
  itemInOrderIds: string[];
  onToggleItem: (itemId: string, quantity?: number) => void;
  showHeading?: boolean;
  showPreference?: boolean;
  items: {
    id: string;
    title: string;
    price: number;
    imageSrc: string;
    imageAlt: string;
    quantity?: number;
    quantityLimit?: number;
    allowMultiple?: boolean;
    soldOut?: boolean;
  }[];
};

export const WorkSelectionGrid = ({
  allowMultipleSelections = false,
  currencyCode,
  enteredItemIds = [],
  isPreOrderFlow = false,
  itemInOrderIds,
  items,
  onToggleItem,
  showHeading = true,
  showPreference = true,
}: WorkSelectionGridProps) => {
  const { t } = useTranslation('checkout');
  const enteredItemIdSet = new Set(enteredItemIds);
  const soldOutItemIdSet = new Set(
    items.filter((item) => item.soldOut).map((item) => item.id),
  );
  const selectableOrderIds = itemInOrderIds.filter(
    (itemId) => !enteredItemIdSet.has(itemId) && !soldOutItemIdSet.has(itemId),
  );
  const description = allowMultipleSelections
    ? items.some((item) => item.allowMultiple !== false)
      ? t('orderReview.buy.selectWorks.description')
      : null
    : t('orderReview.selectWorks.description');

  return (
    <section className="flex w-full flex-col gap-4">
      {showHeading && (
        <div className="flex flex-col text-neutral-6">
          <p className="font-bold">
            {allowMultipleSelections
              ? t('orderReview.buy.selectWorks.title')
              : isPreOrderFlow
                ? t('orderReview.preOrder.selectWorks.title')
                : t('orderReview.selectWorks.title')}
          </p>
          {description ? <p>{description}</p> : null}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {items.map((item) => {
          const isEntered = enteredItemIdSet.has(item.id);
          const isSoldOut = soldOutItemIdSet.has(item.id);
          const rank = selectableOrderIds.indexOf(item.id);
          const isSelected = !isEntered && !isSoldOut && rank !== -1;
          const canPickQuantity =
            allowMultipleSelections && item.allowMultiple !== false;

          let quantity: number | undefined;
          if (isSelected && canPickQuantity) {
            quantity = item.quantity ?? 1;
          } else if (isSelected && showPreference) {
            quantity = rank + 1;
          }

          return (
            <SelectableTile
              key={item.id}
              id={item.id}
              image={{
                src: item.imageSrc,
                alt: item.imageAlt,
                width: 256,
              }}
              isSelected={isSelected}
              maxQuantity={item.quantityLimit}
              status={isSoldOut ? 'soldOut' : isEntered ? 'entered' : undefined}
              onClick={() => onToggleItem(item.id)}
              onQuantityChange={
                canPickQuantity
                  ? (nextQuantity) => onToggleItem(item.id, nextQuantity)
                  : undefined
              }
              price={String(formatPrice(item.price, currencyCode))}
              quantity={quantity}
              title={item.title}
            />
          );
        })}
      </div>
    </section>
  );
};
